import type { A11yPreferencesPatch, PersonaId } from '@/types/a11y-preferences';

export type { PersonaId } from '@/types/a11y-preferences';

export interface PersonaDefinition {
  id: PersonaId;
  labelKey: string;
  descriptionKey: string;
  patch: A11yPreferencesPatch;
}

export const PERSONA_MANAGED_KEYS: (keyof A11yPreferencesPatch)[] = [
  'fontSizePercent',
  'zoomPercent',
  'highContrast',
  'monochrome',
  'readingMask',
  'focusRingEmphasis',
  'underlineLinks',
  'dyslexiaFont',
  'picklistStyle',
  'reducedMotion',
  'customCursor',
  'customScrollbar',
  'highlightCritical',
  'strikethroughDisabled',
  'displayLayout',
];

export const PERSONA_DEFINITIONS: PersonaDefinition[] = [
  {
    id: 'adhdFriendly',
    labelKey: 'accessibility.personas.adhdFriendly.label',
    descriptionKey: 'accessibility.personas.adhdFriendly.description',
    patch: { readingMask: true, reducedMotion: 'on' },
  },
  {
    id: 'astigmatism',
    labelKey: 'accessibility.personas.astigmatism.label',
    descriptionKey: 'accessibility.personas.astigmatism.description',
    patch: { highContrast: true, fontSizePercent: 110 },
  },
  {
    id: 'blindness',
    labelKey: 'accessibility.personas.blindness.label',
    descriptionKey: 'accessibility.personas.blindness.description',
    patch: { focusRingEmphasis: true, underlineLinks: true },
  },
  {
    id: 'colorBlind',
    labelKey: 'accessibility.personas.colorBlind.label',
    descriptionKey: 'accessibility.personas.colorBlind.description',
    patch: { highContrast: true, picklistStyle: 'minimal' },
  },
  {
    id: 'dyslexiaFriendly',
    labelKey: 'accessibility.personas.dyslexiaFriendly.label',
    descriptionKey: 'accessibility.personas.dyslexiaFriendly.description',
    patch: { dyslexiaFont: true, fontSizePercent: 110, underlineLinks: true },
  },
  {
    id: 'epilepsySafe',
    labelKey: 'accessibility.personas.epilepsySafe.label',
    descriptionKey: 'accessibility.personas.epilepsySafe.description',
    patch: { reducedMotion: 'on' },
  },
  {
    id: 'olderUsers',
    labelKey: 'accessibility.personas.olderUsers.label',
    descriptionKey: 'accessibility.personas.olderUsers.description',
    patch: { fontSizePercent: 115, highContrast: true, focusRingEmphasis: true, customCursor: true },
  },
  {
    id: 'lowVision',
    labelKey: 'accessibility.personas.lowVision.label',
    descriptionKey: 'accessibility.personas.lowVision.description',
    patch: { fontSizePercent: 120, zoomPercent: 110, highContrast: true, focusRingEmphasis: true },
  },
  {
    id: 'motorDisabilities',
    labelKey: 'accessibility.personas.motorDisabilities.label',
    descriptionKey: 'accessibility.personas.motorDisabilities.description',
    patch: { focusRingEmphasis: true, customCursor: true, displayLayout: 'comfortable' },
  },
  {
    id: 'seizureSafe',
    labelKey: 'accessibility.personas.seizureSafe.label',
    descriptionKey: 'accessibility.personas.seizureSafe.description',
    patch: { reducedMotion: 'on' },
  },
];

export function mergePersonaPatches(activeIds: PersonaId[]): A11yPreferencesPatch {
  const merged: A11yPreferencesPatch = {};
  for (const def of PERSONA_DEFINITIONS) {
    if (!activeIds.includes(def.id)) continue;
    Object.assign(merged, def.patch);
  }
  return merged;
}
